/**
 * Content Tabs - Reusable Tab System
 * Supports multiple tab sections on the same page
 */

(function() {
  'use strict';

  class ContentTabs {
    constructor(container) {
      this.container = container;
      this.nav = container.querySelector('.content-tabs-nav');
      this.navItems = container.querySelectorAll('.content-tabs-nav-item');
      this.panels = container.querySelectorAll('.content-tabs-panel');
      
      if (!this.nav || !this.navItems.length || !this.panels.length) return;

      this.tabNavBundle = this.nav.closest('.location-content-tabs-scroll-bundle');
      
      this.init();
    }

    init() {
      this.attachEventListeners();
      
      // Preload all images in all tabs immediately (since they're all in DOM)
      this.preloadAllImages();
      
      // Set initial active state
      this.updateActiveState();

      this.initLocationTabNavArrows();
    }

    initLocationTabNavArrows() {
      if (!this.tabNavBundle || !this.nav) return;

      const prev = this.tabNavBundle.querySelector('[data-location-tabs-nav-prev]');
      const next = this.tabNavBundle.querySelector('[data-location-tabs-nav-next]');
      if (!prev || !next) return;

      const scrollAmount = () => Math.max(160, Math.floor(this.nav.clientWidth * 0.65));

      const tabsScrollWidth = () => {
        let sum = 0;
        this.navItems.forEach((el) => {
          sum += el.getBoundingClientRect().width;
        });
        return sum;
      };

      const updateArrows = () => {
        const navW = this.nav.clientWidth;
        const maxScroll = Math.max(0, this.nav.scrollWidth - navW);
        /* scrollWidth can briefly match clientWidth on some mobile browsers after smooth scroll; sum of tab widths is a stable fallback. */
        const overflow =
          maxScroll > 2 || tabsScrollWidth() > navW + 4;
        this.tabNavBundle.classList.toggle('location-content-tabs-scroll-bundle--overflow', overflow);
        if (!overflow) {
          prev.disabled = true;
          next.disabled = true;
          return;
        }
        const end = Math.max(0, this.nav.scrollWidth - navW);
        prev.disabled = this.nav.scrollLeft <= 2;
        next.disabled = end > 2 && this.nav.scrollLeft >= end - 2;
      };

      let scrollRaf = null;
      const scheduleArrowUpdate = () => {
        if (scrollRaf) cancelAnimationFrame(scrollRaf);
        scrollRaf = requestAnimationFrame(() => {
          scrollRaf = null;
          updateArrows();
        });
      };

      prev.addEventListener('click', () => {
        this.nav.scrollBy({ left: -scrollAmount(), behavior: 'smooth' });
      });
      next.addEventListener('click', () => {
        this.nav.scrollBy({ left: scrollAmount(), behavior: 'smooth' });
      });
      this.nav.addEventListener('scroll', scheduleArrowUpdate);
      window.addEventListener('resize', scheduleArrowUpdate);
      if (typeof ResizeObserver !== 'undefined') {
        const ro = new ResizeObserver(scheduleArrowUpdate);
        ro.observe(this.nav);
      }
      updateArrows();
    }

    preloadAllImages() {
      // Preload all images in all tab panels immediately
      this.panels.forEach((panel) => {
        const lazyImages = panel.querySelectorAll('img[data-src]');
        lazyImages.forEach((img) => {
          if (img.dataset.src && !img.src) {
            img.src = img.dataset.src;
            img.classList.add('loaded');
          }
        });
      });
    }

    attachEventListeners() {
      this.navItems.forEach((item) => {
        item.addEventListener('click', (e) => {
          e.preventDefault();
          const targetTab = item.dataset.tab;
          this.switchTab(targetTab);
        });
      });

      // Keyboard navigation
      this.navItems.forEach((item, index) => {
        item.addEventListener('keydown', (e) => {
          let targetIndex = index;
          
          if (e.key === 'ArrowLeft') {
            e.preventDefault();
            targetIndex = index > 0 ? index - 1 : this.navItems.length - 1;
          } else if (e.key === 'ArrowRight') {
            e.preventDefault();
            targetIndex = index < this.navItems.length - 1 ? index + 1 : 0;
          } else if (e.key === 'Home') {
            e.preventDefault();
            targetIndex = 0;
          } else if (e.key === 'End') {
            e.preventDefault();
            targetIndex = this.navItems.length - 1;
          }
          
          if (targetIndex !== index) {
            const targetTab = this.navItems[targetIndex].dataset.tab;
            this.switchTab(targetTab);
            this.navItems[targetIndex].focus();
          }
        });
      });
    }

    switchTab(tabId) {
      // Remove active class from all nav items
      this.navItems.forEach((item) => {
        item.classList.remove('active');
        item.setAttribute('aria-selected', 'false');
      });

      // Hide all panels
      this.panels.forEach((panel) => {
        panel.classList.remove('active');
      });

      // Activate target tab
      const targetNavItem = this.nav.querySelector(`[data-tab="${tabId}"]`);
      const targetPanel = this.container.querySelector(`#${tabId}`);

      if (targetNavItem) {
        targetNavItem.classList.add('active');
        targetNavItem.setAttribute('aria-selected', 'true');
      }

      if (targetPanel) {
        targetPanel.classList.add('active');
      }

      // Location tab strips: keep the selected tab visible within .content-tabs-nav only (avoid scrollIntoView
      // scrolling ancestor/body horizontally)
      if (targetNavItem && this.container.hasAttribute('data-tabs-nav-scroll-start')) {
        requestAnimationFrame(() => {
          const navRect = this.nav.getBoundingClientRect();
          const itemRect = targetNavItem.getBoundingClientRect();
          const targetScroll = this.nav.scrollLeft + (itemRect.left - navRect.left);
          this.nav.scrollTo({ left: targetScroll, behavior: 'smooth' });
        });
      } else if (targetNavItem && window.innerWidth <= 992) {
        requestAnimationFrame(() => {
          const navRect = this.nav.getBoundingClientRect();
          const itemRect = targetNavItem.getBoundingClientRect();
          const targetScroll =
            this.nav.scrollLeft + (itemRect.left - navRect.left) - navRect.width / 2 + itemRect.width / 2;
          this.nav.scrollTo({ left: targetScroll, behavior: 'smooth' });
        });
      }
    }

    updateActiveState() {
      // Ensure first tab is active if none are
      const hasActive = Array.from(this.navItems).some(item => item.classList.contains('active'));
      if (!hasActive && this.navItems.length > 0) {
        const firstTab = this.navItems[0].dataset.tab;
        this.switchTab(firstTab);
      }
    }
  }

  // Initialize all tab sections on page load
  function initTabs() {
    const tabSections = document.querySelectorAll('.content-tabs-section');
    
    tabSections.forEach((section) => {
      new ContentTabs(section);
    });
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initTabs);
  } else {
    initTabs();
  }

  // Re-initialize on Shopify section load (for theme editor)
  if (typeof Shopify !== 'undefined' && Shopify.designMode) {
    document.addEventListener('shopify:section:load', (event) => {
      const section = event.detail.querySelector('.content-tabs-section');
      if (section) {
        new ContentTabs(section);
      }
    });
  }

  /** Givy "Send as Gift" on location tab product cards (.js-givy-card-send-gift) — delegated once */
  let givyCardGiftDelegated = false;
  function initGivyCardSendGiftDelegation() {
    if (givyCardGiftDelegated) return;
    givyCardGiftDelegated = true;
    document.addEventListener(
      'click',
      function (e) {
        const link = e.target.closest && e.target.closest('.js-givy-card-send-gift');
        if (!link) return;
        e.preventDefault();
        const variantId = link.getAttribute('data-givy-variant-id');
        if (window.GIVY && typeof window.GIVY.displayModal === 'function') {
          try {
            window.__givyFlowActive = true;
            window.GIVY.displayModal(variantId);
            document.addEventListener(
              'click',
              function clearGivy(evt) {
                const anchor = document.getElementById('givy-gifting-anchor');
                if (!anchor || !anchor.contains(evt.target)) {
                  window.__givyFlowActive = false;
                  document.removeEventListener('click', clearGivy, true);
                }
              },
              true
            );
          } catch (err) {
            console.warn('Givy error:', err);
          }
        }
      },
      true
    );
  }
  initGivyCardSendGiftDelegation();
})();
